<template>
  <div class="production-schedule">
    <h2 class="no-print">Programa Diario de Produccion</h2>

    <!-- Print Header (only visible when printing) -->
    <div class="print-header">
      <logo-icon class="print-logo" />
      <h1>Programa Diario de Produccion</h1>
      <div class="print-dates" v-if="dates">
        <span><strong>Sucursal:</strong> {{ selectedBranchName }}</span>
        <span><strong>Programacion:</strong> {{ formatDate(dates.programacion) }}</span>
        <span><strong>Produccion:</strong> {{ formatDate(dates.produccion) }}</span>
        <span><strong>Entrega:</strong> {{ formatDate(dates.entrega) }}</span>
      </div>
    </div>

    <div class="card mt-4 no-print">
      <div class="flex flex-wrap gap-4 items-end">
        <div class="flex flex-col">
          <label class="text-sm font-medium">Sucursal</label>
          <select v-model="selectedBranchId" class="mt-1" @change="loadProductionSchedule">
            <option value="">Selecciona una sucursal</option>
            <option v-for="branch in branches" :key="branch.id" :value="branch.id">
              {{ branch.franchise?.name }} - {{ branch.name }}
            </option>
          </select>
        </div>

        <div class="flex flex-col">
          <label class="text-sm font-medium">Fecha de Entrega</label>
          <input
            type="date"
            v-model="deliveryDate"
            class="mt-1"
            @change="loadProductionSchedule"
          />
        </div>

        <div v-if="dates" class="flex gap-6 text-sm">
          <div>
            <span class="text-content-400">Programacion:</span>
            <span class="font-medium ml-1">{{ formatDate(dates.programacion) }}</span>
          </div>
          <div>
            <span class="text-content-400">Produccion:</span>
            <span class="font-medium ml-1">{{ formatDate(dates.produccion) }}</span>
          </div>
          <div>
            <span class="text-content-400">Entrega:</span>
            <span class="font-medium ml-1">{{ formatDate(dates.entrega) }}</span>
          </div>
        </div>

        <button
          v-if="productionList.length > 0"
          @click="printSchedule"
          class="ml-auto btn btn-primary flex items-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          Imprimir
        </button>
      </div>
    </div>

    <div v-if="loadingBranches" class="card mt-4 flex justify-center py-8 no-print">
      <loader-icon class="h-8 w-8 text-primary-500"></loader-icon>
    </div>

    <div v-else-if="productionList.length > 0" class="card mt-4 print-card">
      <div class="flex gap-4 mb-4 text-sm print-legend">
        <div class="flex items-center gap-2">
          <span class="w-3 h-3 rounded-full bg-blue-500 print-dot print-dot-envio"></span>
          <span>Envio ({{ deliveryCount }})</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="w-3 h-3 rounded-full bg-green-500 print-dot print-dot-pickup"></span>
          <span>Pickup ({{ pickupCount }})</span>
        </div>
        <div class="flex items-center gap-2 ml-4">
          <span>Total: {{ productionList.length }}</span>
        </div>
      </div>

      <table class="w-full text-sm">
        <thead>
          <tr class="border-b">
            <th class="py-2 px-3 text-left w-12">No.</th>
            <th class="py-2 px-3 text-left w-20">Entrega</th>
            <th class="py-2 px-3 text-left">Cliente</th>
            <th class="py-2 px-3 text-left">Mascota</th>
            <th class="py-2 px-3 text-left w-20">Tipo</th>
            <th class="py-2 px-3 text-left w-20">Etapa</th>
            <th class="py-2 px-3 text-left w-24">Presentacion</th>
            <th class="py-2 px-3 text-left w-20">Porciones</th>
            <th class="py-2 px-3 text-left w-20">Gramaje</th>
            <th class="py-2 px-3 text-left w-24">Total</th>
            <th class="py-2 px-3 text-left">Especificaciones</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(group, clientId, groupIndex) in groupedByClient" :key="clientId">
            <tr
              v-for="(item, index) in group"
              :key="item.order_id"
              :class="[
                groupIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50',
                index === 0 ? 'border-t-2 border-t-gray-300' : '',
                'hover:bg-blue-50'
              ]"
            >
              <td class="py-2 px-3">{{ getRowNumber(clientId, index) }}</td>
              <td class="py-2 px-3">
                <span
                  class="px-2 py-1 rounded text-xs font-medium"
                  :class="item.pickup_delivery ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'"
                >
                  {{ item.pickup_delivery ? 'Pickup' : 'Envio' }}
                </span>
              </td>
              <td class="py-2 px-3">
                <span v-if="index === 0" class="font-semibold text-gray-900 client-name">{{ item.cliente }}</span>
                <span v-else class="text-gray-400 pl-2">↳</span>
              </td>
              <td class="py-2 px-3 font-medium">{{ item.mascota }}</td>
              <td class="py-2 px-3">{{ item.tipo_mascota }}</td>
              <td class="py-2 px-3">{{ item.etapa }}</td>
              <td class="py-2 px-3">{{ item.textura }}</td>
              <td class="py-2 px-3">{{ item.porciones }}</td>
              <td class="py-2 px-3">{{ item.gramaje }}g</td>
              <td class="py-2 px-3 font-medium">{{ totalGramaje(item) }}</td>
              <td class="py-2 px-3 text-content-400">{{ item.especificaciones }}</td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div v-else-if="selectedBranchId && deliveryDate && !loadingSchedule" class="card mt-4 no-print">
      <p class="text-content-400 text-center py-8">
        No hay ordenes programadas para esta fecha de entrega.
      </p>
    </div>

    <div v-if="loadingSchedule" class="card mt-4 flex justify-center py-8 no-print">
      <loader-icon class="h-8 w-8 text-primary-500"></loader-icon>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import LogoIcon from "@/icons/LogoIcon.vue";

export default {
  name: "ProductionSchedule",

  components: {
    LogoIcon,
  },

  data() {
    return {
      loadingBranches: false,
      loadingSchedule: false,
      branches: [],
      selectedBranchId: "",
      deliveryDate: "",
      productionList: [],
      dates: null,
    };
  },

  computed: {
    groupedByClient() {
      const groups = {};
      this.productionList.forEach((item) => {
        if (!groups[item.client_id]) {
          groups[item.client_id] = [];
        }
        groups[item.client_id].push(item);
      });
      return groups;
    },

    deliveryCount() {
      return this.productionList.filter((item) => !item.pickup_delivery).length;
    },

    pickupCount() {
      return this.productionList.filter((item) => item.pickup_delivery).length;
    },

    selectedBranchName() {
      const branch = this.branches.find(b => b.id === this.selectedBranchId);
      return branch ? `${branch.franchise?.name} - ${branch.name}` : '';
    },
  },

  created() {
    this.$store.commit("setSelectedTab", "productionSchedule");
    this.loadBranches();
    this.setDefaultDate();
  },

  methods: {
    totalGramaje(item) {
      const total = Number(item.porciones || 0) * Number(item.gramaje || 0);
      return total.toLocaleString("es-MX") + "g";
    },

    getRowNumber(clientId, index) {
      let count = 0;
      for (const [id, group] of Object.entries(this.groupedByClient)) {
        if (id === clientId) {
          return count + index + 1;
        }
        count += group.length;
      }
      return count + 1;
    },

    setDefaultDate() {
      const today = new Date();
      today.setDate(today.getDate() + 3);
      this.deliveryDate = today.toISOString().split("T")[0];
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString + "T00:00:00");
      return date.toLocaleDateString("es-MX", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    async loadBranches() {
      this.loadingBranches = true;

      try {
        this.branches = await api.branches.getAll();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message || "Error al cargar sucursales");
      }

      this.loadingBranches = false;
    },

    async loadProductionSchedule() {
      if (!this.selectedBranchId || !this.deliveryDate) {
        this.productionList = [];
        this.dates = null;
        return;
      }

      this.loadingSchedule = true;

      try {
        const response = await api.productionSchedule.getByDeliveryDate(
          this.selectedBranchId,
          this.deliveryDate
        );
        this.productionList = response.data;
        this.dates = response.dates;
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message || "Error al cargar el programa");
        this.productionList = [];
        this.dates = null;
      }

      this.loadingSchedule = false;
    },

    printSchedule() {
      const originalTitle = document.title;
      const productionDate = this.dates?.produccion || this.deliveryDate;
      document.title = `Trunimal_Programacion_Para_${productionDate}`;
      window.print();
      document.title = originalTitle;
    },
  },
};
</script>

<style>
@media print {
  /* Hide everything except the production schedule */
  body * {
    visibility: hidden;
  }

  .production-schedule,
  .production-schedule * {
    visibility: visible;
  }

  .production-schedule {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    padding: 6px;
  }

  /* Hide non-print elements */
  .no-print {
    display: none !important;
  }

  /* Show print header */
  .print-header {
    display: block !important;
    text-align: center;
    margin-bottom: 6px;
  }

  .print-logo {
    width: 64px;
    height: auto;
    margin: 0 auto 3px;
    display: block;
  }

  .print-header h1 {
    font-size: 13px;
    font-weight: bold;
    margin-bottom: 3px;
  }

  .print-dates {
    display: flex;
    justify-content: center;
    gap: 12px;
    font-size: 9px;
    flex-wrap: wrap;
  }

  /* Card styling for print */
  .print-card {
    box-shadow: none !important;
    border: none !important;
    padding: 0 !important;
  }

  /* Table styling for print */
  .print-card table {
    width: 100%;
    border-collapse: collapse;
    font-size: 9px;
  }

  .print-card th,
  .print-card td {
    border: 1px solid #ccc;
    padding: 1px 3px;
    line-height: 1.25;
  }

  /* El nombre del cliente es lo que mas ancho consume: mas chico y sin negrita pesada */
  .print-card .client-name {
    font-size: 8px;
    font-weight: 600;
  }

  /* Badges Pickup/Envio sin el padding de pantalla */
  .print-card td span[class*="rounded"] {
    padding: 0 3px !important;
    font-size: 8px !important;
  }

  .print-card th {
    background-color: #f3f4f6 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .print-card tr {
    border: none !important;
  }

  /* Remove hover effects */
  .print-card tr:hover {
    background-color: transparent !important;
  }

  /* Alternating row colors for print */
  .print-card tbody tr:nth-child(even) {
    background-color: #f9fafb !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* Legend styling */
  .print-legend {
    margin-bottom: 4px !important;
    font-size: 9px;
  }

  .print-dot {
    width: 10px !important;
    height: 10px !important;
    border-radius: 50%;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .print-dot-envio {
    background-color: #3b82f6 !important;
  }

  .print-dot-pickup {
    background-color: #22c55e !important;
  }

  /* Delivery type badges */
  .print-card td span[class*="bg-green"] {
    background-color: #dcfce7 !important;
    color: #15803d !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .print-card td span[class*="bg-blue"] {
    background-color: #dbeafe !important;
    color: #1d4ed8 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}

/* Hide print header on screen */
.print-header {
  display: none;
}
</style>
